import { React, useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { DYNAMIC_TABLE, LIST, getApiPath } from '../../api/ApiConst';
import TableCreate from './TableCreate';
import TableFilter from '../constants/TableFilter';
import { useClient } from '../../config/ClientProvider';
import TableImport from './TableImport';

const TableList = () => {
	const { selectedClient } = useClient();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [drawCount, setDrawCount] = useState(0);
	const [showAddModel, setShowAddModel] = useState(false);
	const [showImportModel, setShowImportModel] = useState(false);

	// Get pagination state from URL
	const currentPage = parseInt(searchParams.get('page')) || 1;
	const pageSize = parseInt(searchParams.get('size')) || 10;
	const searchTerm = searchParams.get('search') || '';
	const sortField = searchParams.get('sortField') || '';
	const sortOrder = searchParams.get('sortOrder') || '';

	// Debug logging to see what's happening
	console.log('TableList Mount/Update:', {
		currentPage,
		pageSize,
		searchTerm,
		sortField,
		sortOrder,
		fullUrl: window.location.href,
		pathname: window.location.pathname,
		search: window.location.search,
		timestamp: new Date().toISOString()
	});

	// Update URL with pagination state
	const updatePaginationState = useCallback((newPage, newSize, newSearch = '', newSortField = '', newSortOrder = '') => {
		const params = new URLSearchParams();
		if (newPage > 1) params.set('page', newPage.toString());
		if (newSize !== 10) params.set('size', newSize.toString());
		if (newSearch) params.set('search', newSearch);
		if (newSortField) params.set('sortField', newSortField);
		if (newSortOrder) params.set('sortOrder', newSortOrder);
		setSearchParams(params, { replace: true });
	}, [setSearchParams]);

	// Sync URL state with component state on mount and URL changes
	useEffect(() => {
		// Force refresh when URL parameters change
		setDrawCount((prev) => (prev + 1));
	}, [currentPage, pageSize, searchTerm, sortField, sortOrder]);
	let rObj = {
		name: "",
		description: "",
		pathValue: "",
		addDefaultAttribute: false,
		isMultiTenantTable: false,
	};


	// Table Creation Model show/close
	const toggleAddModel = () => {
		setShowAddModel(prev => !prev);
	};

	// Table Import Model show/close
	const toggleImportModel = () => {
		setShowImportModel(prev => !prev);
	};

	// Create Table Record
	const handleCreateTableRecord = () => {
		toggleAddModel();
	};

	// Navigate to Table Records View

	// Refresh Table
	const refreshTableData = useCallback(() => {
		setDrawCount((prev) => (prev + 1));
	}, []);

	// Handle pagination change
	const handlePaginationChange = useCallback((page, size, search = '', sortField = '', sortOrder = '') => {
		updatePaginationState(page, size, search, sortField, sortOrder);
		setDrawCount((prev) => (prev + 1));
	}, [updatePaginationState]);

	// get Table Records View
	const getTableView = useCallback(() => {
		return (
			<>
				{selectedClient ? (
					<div className="row justify-content-end">
						<div className="col-12 px-0">
							<div className="card">
								<div className="card-body d-flex justify-content-end" style={{ textAlign: "right" }}>
									<button className="btn btn-primary me-2" onClick={toggleImportModel}>
										Import
									</button>
									<button className="btn btn-primary" onClick={handleCreateTableRecord}>
										Create New
									</button>
								</div>
								<TableFilter
									tableName="table"
									tableUrl={getApiPath(DYNAMIC_TABLE, LIST)}
									paginationStatus={true}
									drawCount={drawCount}
									refreshTableData={refreshTableData}
									// URL-based pagination props
									currentPage={currentPage}
									pageSize={pageSize}
									searchTerm={searchTerm}
									sortField={sortField}
									sortOrder={sortOrder}
									onPaginationChange={handlePaginationChange}
								/>
							</div>
						</div>
					</div>
				) : (
					<p>Please select a client to view data.</p>
				)}
			</>
		);
	}, [drawCount, selectedClient, currentPage, pageSize, searchTerm, sortField, sortOrder]);

	// useEffect to listen for selectedClient changes
	useEffect(() => {
		if (selectedClient) {
			// Any logic to re-fetch data or trigger updates when selectedClient changes
			refreshTableData();
		}
	}, [selectedClient, refreshTableData]);

	// Handle initial load and URL changes
	useEffect(() => {
		// Ensure data is loaded when component mounts or URL changes
		if (selectedClient) {
			refreshTableData();
		}
	}, [selectedClient, currentPage, pageSize, refreshTableData]);

	// Simple effect to ensure data loads when URL parameters change
	useEffect(() => {
		if (selectedClient) {
			refreshTableData();
		}
	}, [selectedClient, currentPage, pageSize, searchTerm, sortField, sortOrder, refreshTableData]);

	return (
		<>
			{getTableView()}
			<TableCreate
				show={showAddModel}
				close={toggleAddModel}
				successCallBack={refreshTableData}
				object={rObj}
			/>
			<TableImport
				show={showImportModel}
				close={toggleImportModel}
				successCallBack={refreshTableData}
			/>
		</>
	);
};

export default TableList;
